import React, { useState, useEffect } from 'react';
import { Tabs, Card, Spin, Alert, Form } from 'antd';
import { CalendarOutlined, SoundOutlined, TeamOutlined, BookOutlined, UserOutlined, ScheduleOutlined } from '@ant-design/icons';
import { fetchProfiles, searchProfilesMeili } from '../server/profiles/profilesServer';
import { fetchMusicGenres } from '../server/configuration/MusicGenresServer';
import TrackFormModal from '../components/TrackFormModal';
import { 
  EventScraper, 
  MusicScraper, 
  ArtistCatalog, 
  StoryNews, 
  ProfileScraper,
  EventCalendar 
} from './scraper/index';

const { TabPane } = Tabs;

const Scraper = () => {
  // General state
  const [activeTab, setActiveTab] = useState('events');
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [musicGenres, setMusicGenres] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Event scraper state
  const [eventData, setEventData] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  
  // Music scraper state
  const [trackData, setTrackData] = useState(null);
  const [showTrackForm, setShowTrackForm] = useState(false);
  const [trackForm] = Form.useForm();

  // Search profiles using MeiliSearch
  const searchProfiles = async (query = '', additionalFilters = {}) => {
    try {
      const filters = {
        isActive: true,
        isVerified: true,
        limit: 50,
        offset: 0,
        ...additionalFilters
      };
      
      const response = await searchProfilesMeili(query, filters);
      return response.data.filter(profile => profile.profileType !== 'ML'); // Exclude Music Lovers
    } catch (error) {
      console.error('Error searching profiles:', error);
      return [];
    }
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load music genres and initial profiles
        const [musicGenresResponse, initialProfiles] = await Promise.all([
          fetchMusicGenres(),
          searchProfiles('', { limit: 20 }) // Load 20 initial profiles
        ]);
        
        if (initialProfiles) {
          setProfiles(initialProfiles);
        }
        
        if (musicGenresResponse && musicGenresResponse.data) {
          setMusicGenres(musicGenresResponse.data);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setProfilesLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle modal close
  const handleTrackFormCancel = () => {
    setShowTrackForm(false);
    trackForm.resetFields();
    setTrackData(null);
  };

  const tabItems = [
    {
      key: 'events',
      label: (
        <span>
          <CalendarOutlined />
          Event Scraper
        </span>
      ),
      children: (
        <EventScraper
          profiles={profiles}
          profilesLoading={profilesLoading}
          musicGenres={musicGenres}
          eventData={eventData}
          setEventData={setEventData}
          showEventForm={showEventForm}
          setShowEventForm={setShowEventForm}
          selectedProfile={selectedProfile}
          setSelectedProfile={setSelectedProfile}
          searchProfiles={searchProfiles}
        />
      ),
    },
    {
      key: 'calendar',
      label: (
        <span>
          <ScheduleOutlined />
          Events Calendar
        </span>
      ),
      children: (
        <EventCalendar />
      ),
    },
    {
      key: 'music',
      label: (
        <span>
          <SoundOutlined />
          Music Scraper
        </span>
      ),
      children: (
        <MusicScraper
          musicGenres={musicGenres}
          selectedProfile={selectedProfile}
          setSelectedProfile={setSelectedProfile}
          showTrackForm={showTrackForm}
          setShowTrackForm={setShowTrackForm}
          trackForm={trackForm}
          setTrackData={setTrackData}
        />
      ),
    },
    {
      key: 'catalog',
      label: (
        <span>
          <TeamOutlined />
          Artist Catalog
        </span>
      ),
      children: (
        <ArtistCatalog
          profiles={profiles}
          profilesLoading={profilesLoading}
          selectedProfile={selectedProfile}
          setSelectedProfile={setSelectedProfile}
          showTrackForm={showTrackForm}
          setShowTrackForm={setShowTrackForm}
          trackForm={trackForm}
          setTrackData={setTrackData}
          musicGenres={musicGenres}
          searchProfiles={searchProfiles}
        />
      ),
    },
    {
      key: 'stories',
      label: (
        <span>
          <BookOutlined />
          Story News
        </span>
      ),
      children: (
        <StoryNews
          profiles={profiles}
          profilesLoading={profilesLoading}
          searchProfiles={searchProfiles}
        />
      ),
    },
    {
      key: 'profiles',
      label: (
        <span>
          <UserOutlined />
          Profile Scraper
        </span>
      ),
      children: (
        <ProfileScraper
          musicGenres={musicGenres}
        />
      ),
    },
  ];

  if (profilesLoading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Card className="w-full max-w-md">
          <div className="text-center">
            <Spin size="large" />
            <p className="mt-4 text-gray-600">Loading profiles and music genres...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="scraper-page">
      <Card 
        title="Content Scraper Tools" 
        className="mb-6"
        style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <Alert
          message="Scraping Tools"
          description="Use these tools to extract and import content from various platforms into your system. Each tab provides specialized scraping functionality for different content types."
          type="info"
          showIcon
          className="mb-4"
        />
        
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          size="large"
          items={tabItems}
          className="scraper-tabs"
        />
      </Card>

      {/* Track Publication Modal */}
      <TrackFormModal
        visible={showTrackForm}
        onCancel={handleTrackFormCancel}
        form={trackForm}
        musicGenres={musicGenres}
        selectedProfile={selectedProfile}
        profiles={profiles}
        profilesLoading={profilesLoading}
        searchProfiles={searchProfiles}
      />
    </div>
  );
};

export default Scraper; 